import { Directive, inject } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FormStateService } from '../../services/formly/form-state.service';

/**
 * Base class that gives any ngx-formly field a single, safe way to report a
 * lookup outcome for analytics.
 *
 * Components extend this instead of `FieldType` directly and call
 * `emitFetchOutcome('success' | 'failure', error?)` at their outcome terminals.
 *
 * Data-leak safeguard: this is the ONLY emit path. The forwarded payload is a
 * fixed allow-list (outcome, fieldType, fetchKey, and on failure errorMessage/
 * errorCode) — never the lookup response body or the value the user typed.
 *
 * The emit is gated on `field.props.mixpanelEventAnalytics` (the single toggle a
 * service manager sets), so untracked fields cost nothing.
 */
@Directive()
export class AnalyticsAwareFieldType<
  F extends FieldTypeConfig = FieldTypeConfig
> extends FieldType<F> {
  // Distinct name from any subclass-injected `formStateService` (same root
  // singleton either way) to avoid colliding with existing constructor injections.
  protected readonly analyticsFormState = inject(FormStateService);

  protected emitFetchOutcome(
    outcome: 'success' | 'failure',
    error?: any,
    displayedErrorMessage?: unknown
  ): void {
    if (!this.field?.props?.['mixpanelEventAnalytics']) {
      return;
    }
    this.analyticsFormState.emitFetchEvent({
      outcome,
      fieldType: (this.field.type as string) ?? '',
      fetchKey: (this.field.key as string) ?? '',
      ...(outcome === 'failure'
        ? {
            errorMessage: this.resolveDisplayedErrorMessage(
              error,
              displayedErrorMessage
            ),
            errorCode: error?.status ?? error?.code,
          }
        : {}),
    });
  }

  /**
   * Analytics must mirror the message visible in the UI, never Angular's raw
   * HttpErrorResponse text. Components expose that message either through a
   * display-state property or through their active Formly validation error.
   */
  private resolveDisplayedErrorMessage(
    error: any,
    displayedErrorMessage?: unknown
  ): string | undefined {
    const explicitMessage = this.resolveMessage(displayedErrorMessage);
    if (explicitMessage) return explicitMessage;

    const componentState = this as unknown as Record<string, unknown>;
    for (const property of [
      'errorMessage',
      'dppError',
      'failureMessage',
      'responseErrorMessage',
    ]) {
      const stateMessage = this.resolveMessage(componentState[property]);
      if (stateMessage) return stateMessage;
    }

    const controlErrors = this.formControl?.errors ?? {};
    for (const [errorKey, errorValue] of Object.entries(controlErrors)) {
      const inlineMessage = this.resolveMessage(
        (errorValue as { message?: unknown })?.message,
        errorValue
      );
      if (inlineMessage) return inlineMessage;

      const validationMessage = this.resolveMessage(
        this.field.validation?.messages?.[errorKey],
        errorValue
      );
      if (validationMessage) return validationMessage;
    }

    if (componentState['dataFetchedFailedError']) {
      const otpFailureMessage = this.resolveMessage(
        this.field.validation?.messages?.['dataFetchFailed']
      );
      if (otpFailureMessage) return otpFailureMessage;
    }

    const isHttpError =
      error?.status !== undefined ||
      error?.name === 'HttpErrorResponse' ||
      typeof error?.url === 'string' ||
      String(error?.message ?? '').startsWith('Http failure response for');

    if (isHttpError) return undefined;

    return this.resolveMessage(error?.error?.message ?? error?.message);
  }

  private resolveMessage(
    message: unknown,
    errorValue?: unknown
  ): string | undefined {
    if (typeof message === 'function') {
      try {
        return this.resolveMessage(message(errorValue, this.field));
      } catch {
        return undefined;
      }
    }

    return typeof message === 'string' && message.trim() ? message : undefined;
  }
}
